import React, { useCallback, memo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import _ from "lodash";
import { Checkbox, Avatar } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import { useGradesContext } from "../../context/GradesContext";
import {
  GRADE_ROW_HEIGHT,
  PARTICIPANT_USER_TYPES,
  USER_TYPE_COUNT_FIELDS,
} from "../../modules/GradesConstants";
import GradeRowSkeleton from "./GradeRowSkeleton";
import classes from "./GradeRow.scss";

const GRADE_ROW_STYLE = { minHeight: GRADE_ROW_HEIGHT };

const GradeRow = ({ gradeId, testId }) => {
  const { t } = useI18n(["common"]);
  const {
    gradeDetailsMap,
    registerGradeRef,
    selectedGradeList,
    setSelectedGradeList,
    viewOnly,
    selectedUserTypes,
  } = useGradesContext();

  const gradeDetails = _.get(gradeDetailsMap, gradeId);
  const isDetailsLoaded = !!gradeDetails;
  const isChecked = _.some(selectedGradeList, { id: gradeId });

  const handleRef = useCallback(
    element => {
      registerGradeRef(gradeId, element);
    },
    [registerGradeRef, gradeId]
  );

  const handleClick = useCallback(() => {
    if (viewOnly) return;

    if (isChecked) {
      setSelectedGradeList(prev => _.filter(prev, item => item.id !== gradeId));
    } else {
      setSelectedGradeList(prev => [
        ...prev,
        { id: gradeId, name: _.get(gradeDetails, "name", "") },
      ]);
    }
  }, [viewOnly, isChecked, gradeId, gradeDetails, setSelectedGradeList]);

  const componentTestId = testId ? `${testId}-gradeRow-${gradeId}` : undefined;

  // State A: no details fetched yet
  if (!isDetailsLoaded) {
    return (
      <div
        ref={handleRef}
        data-id={gradeId}
        className={classes.gradeRow}
        style={GRADE_ROW_STYLE}
      >
        <GradeRowSkeleton />
      </div>
    );
  }

  const name = _.get(gradeDetails, "name", "");
  const hasAllCounts =
    gradeDetails.staffCount !== undefined &&
    gradeDetails.studentCount !== undefined &&
    gradeDetails.familyMemberCount !== undefined;

  return (
    <div
      ref={handleRef}
      data-id={gradeId}
      className={classes.gradeRow}
      style={GRADE_ROW_STYLE}
      onClick={viewOnly ? null : handleClick}
      onKeyDown={viewOnly ? null : e => onEnterOrSpaceKeyDown(e, handleClick)}
      role="button"
      tabIndex={0}
      data-test-id={`${componentTestId}-checkbox`}
    >
      <div className={classes.leftContainer}>
        <Avatar dsVersion="2.0" name={name} shape="square" size="medium" />
        <div className={classes.gradeInfo}>
          <span className={classNames("text-body", classes.gradeTitle)}>
            {name}
          </span>
          {/* State B: name loaded, counts still loading */}
          {!hasAllCounts ? (
            <SkeletonLoader width="360px" height="16px" />
          ) : (
            <div className={classes.countRow}>
              {_.map(selectedUserTypes, (userType, index) => {
                const countField = USER_TYPE_COUNT_FIELDS[userType];
                const count = _.get(gradeDetails, countField, 0);
                return (
                  <span
                    className={classNames("text-body-s", classes.countText)}
                    key={`${gradeId}-${userType}`}
                  >
                    {index ? ", " : ""}
                    {`${count} ${t(PARTICIPANT_USER_TYPES[userType], {
                      count,
                    })}`}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {viewOnly ? null : (
        <Checkbox isChecked={isChecked} testId={componentTestId} />
      )}
    </div>
  );
};

GradeRow.propTypes = {
  gradeId: PropTypes.string.isRequired,
  testId: PropTypes.string,
};

export default memo(GradeRow);
